import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { dispatchMetadataEvent } from "../../../lib/metadata-history/dispatch";
import {
  ArtMetadataSchema,
  type ArtMetadata,
} from "../../../lib/schedule-presets/art-metadata";
import { useScheduleStore } from "../../../stores/schedule-store";
import { useMetadataEditorNavigationStore } from "../../../stores/metadata-editor-navigation-store";
import { useDayLayoutSyncStore } from "../../../stores/day-layout-sync-store";
import { Button } from "../../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Textarea } from "../../ui/textarea";
import { MetadataForm } from "./MetadataForm";
import { MissingFonts } from "./MissingFonts";
import { SaveMetadataButton } from "./SaveMetadataButton";
import { metadataToJson } from "../../../lib/schedule-presets/metadata-json";
import { PanelLayout } from "../../ui/panel-layout";
import { Check, Plus, Undo2 } from "lucide-react";
import { openMetadataBuilder } from "../../../lib/metadata-builder/session";

export function MetadataEditor({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const { tab, setTab } = useMetadataEditorNavigationStore();
  const id = useId();
  const presetId = useScheduleStore((state) => state.preset.id);
  const liveMetadata = useScheduleStore(
    (state) => state.canvasMetadata ?? state.preset.metadata,
  );
  const liveJson = useMemo(() => metadataToJson(liveMetadata), [liveMetadata]);
  const [draft, setDraft] = useState(liveJson);
  const [formMetadata, setFormMetadata] = useState<ArtMetadata | null>(
    liveMetadata,
  );
  const syncedJson = useRef(liveJson);
  const syncPreset = useRef(presetId);
  const parsed = useMemo(() => {
    try {
      return ArtMetadataSchema.safeParse(JSON.parse(draft));
    } catch {
      return null;
    }
  }, [draft]);
  const [failed, setFailed] = useState(false);
  const invalid = failed || !parsed?.success;
  // Live toolbar updates can arrive before the draft syncs; they are already applied.
  const draftHasEdits = draft !== syncedJson.current;
  const dirty = draftHasEdits && draft !== liveJson;
  const validationMessages = {
    missingDefinition: t("metadata.error.missingDefinition"),
    missingStyle: t("metadata.error.missingStyle"),
    missingFormat: t("metadata.error.missingFormat"),
    textFormat: t("metadata.error.textFormat"),
  };

  const resetToLive = () => {
    syncedJson.current = liveJson;
    setDraft(liveJson);
    setFormMetadata(liveMetadata);
    setFailed(false);
  };

  useEffect(() => {
    const draftWasSynced = draft === syncedJson.current;
    syncedJson.current = liveJson;
    if (!draftWasSynced) return;
    resetToLive();
  }, [liveJson]);

  useEffect(resetToLive, [presetId]);

  useEffect(() => {
    if (syncPreset.current !== presetId) {
      syncPreset.current = presetId;
      if (liveMetadata) useDayLayoutSyncStore.getState().observe(presetId, liveMetadata.days);
      return;
    }
    if (formMetadata) useDayLayoutSyncStore.getState().observe(presetId, formMetadata.days);
  }, [presetId, formMetadata]);

  const editForm = (value: ArtMetadata) => {
    setFailed(false);
    setFormMetadata(value);
    setDraft(metadataToJson(value));
  };

  const apply = () => {
    try {
      const metadata = ArtMetadataSchema.parse(JSON.parse(draft));
      const json = metadataToJson(metadata);
      syncedJson.current = json;
      setDraft(json);
      setFormMetadata(metadata);
      dispatchMetadataEvent({ type: "metadata/replace", metadata });
      setFailed(false);
    } catch (error) {
      console.error("Failed to apply metadata JSON:", error);
      setFailed(true);
    }
  };

  return (
    <PanelLayout
      className="min-h-0 flex-1 border-t border-neutral-200 pt-4"
      bodyClassName="space-y-3"
      header={enabled && <h3 className="text-xs font-semibold">{t("metadata.title")}</h3>}
      footer={<>
        <Button variant="action" size="sm" onClick={openMetadataBuilder}><Plus />{t("builder.open")}</Button>
        {enabled && <>
          <Button variant="action" size="sm" onClick={apply} disabled={!dirty || !parsed?.success}><Check />{t("metadata.apply")}</Button>
          <Button variant="action" size="sm" onClick={resetToLive} disabled={!dirty}><Undo2 />{t("metadata.sync")}</Button>
          {process.env.NODE_ENV !== "production" && <SaveMetadataButton key={presetId} presetId={presetId} draft={draft} valid={parsed?.success === true} />}
        </>}
      </>}
    >
      {enabled && <div className="space-y-3">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label={t("metadata.editorMode")} className="w-full">
          <TabsTrigger value="elements" className="flex-1">{t("metadata.elementsTab")}</TabsTrigger>
          <TabsTrigger value="styles" className="flex-1">{t("metadata.stylesTab")}</TabsTrigger>
          <TabsTrigger value="formats" className="flex-1">{t("metadata.formatsTab")}</TabsTrigger>
          <TabsTrigger value="json" className="flex-1">{t("metadata.jsonTab")}</TabsTrigger>
        </TabsList>
        <TabsContent value="elements">
          {formMetadata ? (
            <MetadataForm
              metadata={formMetadata}
              onChange={editForm}
              section="elements"
            />
          ) : (
            <p role="alert" className="text-xs text-red-600">
              {t("metadata.formUnavailable")}
            </p>
          )}
        </TabsContent>
        <TabsContent value="styles">
          {formMetadata ? (
            <MetadataForm metadata={formMetadata} onChange={editForm} section="styles" />
          ) : (
            <p role="alert" className="text-xs text-red-600">{t("metadata.formUnavailable")}</p>
          )}
        </TabsContent>
        <TabsContent value="formats">
          {formMetadata ? (
            <MetadataForm metadata={formMetadata} onChange={editForm} section="formats" />
          ) : (
            <p role="alert" className="text-xs text-red-600">{t("metadata.formUnavailable")}</p>
          )}
        </TabsContent>
        <TabsContent value="json" className="space-y-2">
          <label htmlFor={id} className="block text-xs font-medium">
            {t("metadata.jsonTab")}
          </label>
          <Textarea
            id={id}
            value={draft}
            spellCheck={false}
            wrap="off"
            className="h-80 resize-y font-mono text-xs"
            aria-invalid={invalid}
            aria-describedby={invalid ? `${id}-error` : undefined}
            onChange={(event) => {
              const nextDraft = event.target.value;
              setDraft(nextDraft);
              try {
                const result = ArtMetadataSchema.safeParse(
                  JSON.parse(nextDraft),
                );
                setFormMetadata(result.success ? result.data : null);
              } catch {
                setFormMetadata(null);
              }
              setFailed(false);
            }}
          />
        </TabsContent>
      </Tabs>
      <MissingFonts json={draft} />
      {invalid && (
        <div
          id={`${id}-error`}
          role="alert"
          className="space-y-1 text-xs text-red-600"
        >
          <p>{t("metadata.invalid")}</p>
          {parsed &&
            !parsed.success &&
            parsed.error.issues.map((issue, index) => {
              const message =
                validationMessages[
                  issue.message as keyof typeof validationMessages
                ];
              const invalidBinding =
                issue.path[0] === "globals" && issue.path[2] === "bind";
              const detail =
                message ??
                (invalidBinding
                  ? t("metadata.error.binding")
                  : t("metadata.error.field"));
              return (
                <p key={index}>
                  {issue.path.join(".")}: {detail}
                </p>
              );
            })}
        </div>
      )}
      <p className="text-xs text-neutral-500">{t("metadata.dragHelp")}</p>
      </div>}
    </PanelLayout>
  );
}
